import { getWeaponMasteryEntitlements, getSelectionsForSource } from './weaponMastery';
import { FIGHTING_STYLE_CLASSES, hasFightingStyleFeature, getClassFightingStyle, canChangeClassFightingStyle, getAdditionalFightingStyle } from '../../../shared/rules/fightingStyle.mjs';
import { hasAdditionalFightingStyle, canChangeAdditionalFightingStyle } from '../../../shared/rules/champion.mjs';
import { needsHuntersPrey, getHuntersPrey, canChangeHuntersPrey, needsDefensiveTactics, getDefensiveTactics, canChangeDefensiveTactics } from '../../../shared/rules/subclasses/ranger/huntersPrey.mjs';
import { getMagicInitiateLists, getUnchosenMagicInitiateLists, canChangeMagicInitiate } from '../../../shared/rules/magicInitiate.mjs';
import { getClericLevel, needsBlessedStrikes } from '../../../shared/rules/cleric.mjs';
import { getDivineOrder } from '../../../shared/rules/divineOrder';
import { getPrimalOrder } from '../../../shared/rules/primalOrder';
import { getDruidLevel, needsElementalFury } from '../../../shared/rules/druid.mjs';
import { canSwapKnownForm, getKnownForms, needsKnownForms } from '../../../shared/rules/beastForm';
import { canChangeLand, getLand, needsLand } from '../../../shared/rules/subclasses/druid/land.mjs';
import { needsSignatureSpells, needsSpellMastery, needsSpellbookSpells } from '../../../shared/rules/wizard.mjs';
import { canChangeMagicalDiscoveries, getMagicalDiscoveries, needsBonusProficiencies, needsMagicalDiscoveries } from '../../../shared/rules/subclasses/bard/lore.mjs';
import spellCatalog from '../../../data/spells';

// Keys identify the configurable feature cards in the Character Features dialog.
export const weaponMasteryChoiceKey = (sourceId) => `weapon-mastery:${sourceId}`;
export const FIGHTING_STYLE_CHOICE_KEY = 'fighting-style';
/** Fighter keeps the original key; other classes with a Fighting Style get their own. */
export const fightingStyleChoiceKey = (classId = 'fighter') => (classId === 'fighter' ? FIGHTING_STYLE_CHOICE_KEY : `${FIGHTING_STYLE_CHOICE_KEY}:${classId}`);
/** Additional Fighting Style (Champion 7). */
export const ADDITIONAL_FIGHTING_STYLE_CHOICE_KEY = `${FIGHTING_STYLE_CHOICE_KEY}:additional`;
const fightingStyleClasses = Object.keys(FIGHTING_STYLE_CLASSES);
export const HUNTERS_PREY_CHOICE_KEY = 'hunters-prey';
export const DEFENSIVE_TACTICS_CHOICE_KEY = 'defensive-tactics';
export const DIVINE_ORDER_CHOICE_KEY = 'divine-order';
export const BLESSED_STRIKES_CHOICE_KEY = 'blessed-strikes';
export const PRIMAL_ORDER_CHOICE_KEY = 'primal-order';
export const ELEMENTAL_FURY_CHOICE_KEY = 'elemental-fury';
export const KNOWN_FORMS_CHOICE_KEY = 'known-forms';
export const LAND_CHOICE_KEY = 'land';
export const SPELLBOOK_CHOICE_KEY = 'wizard-spellbook';
export const SPELL_MASTERY_CHOICE_KEY = 'spell-mastery';
export const SIGNATURE_SPELLS_CHOICE_KEY = 'signature-spells';
export const BONUS_PROFICIENCIES_CHOICE_KEY = 'bard-bonus-proficiencies';
export const MAGICAL_DISCOVERIES_CHOICE_KEY = 'bard-magical-discoveries';
export const magicInitiateChoiceKey = (list) => `magic-initiate:${list}`;

/** Feature choices the player still has to make (shown with the gold "needs attention" glow). */
export const getPendingFeatureChoices = (character) => {
  const pending = new Set();
  getWeaponMasteryEntitlements(character).forEach(({ sourceId, count }) => {
    if (getSelectionsForSource(character, sourceId).length < count) pending.add(weaponMasteryChoiceKey(sourceId));
  });
  fightingStyleClasses.forEach((classId) => {
    if (hasFightingStyleFeature(character, classId) && !getClassFightingStyle(character, classId)) pending.add(fightingStyleChoiceKey(classId));
  });
  if (hasAdditionalFightingStyle(character) && !getAdditionalFightingStyle(character)) pending.add(ADDITIONAL_FIGHTING_STYLE_CHOICE_KEY);
  if (needsHuntersPrey(character)) pending.add(HUNTERS_PREY_CHOICE_KEY);
  if (needsDefensiveTactics(character)) pending.add(DEFENSIVE_TACTICS_CHOICE_KEY);
  if (getClericLevel(character) > 0 && !getDivineOrder(character)) pending.add(DIVINE_ORDER_CHOICE_KEY);
  if (needsBlessedStrikes(character)) pending.add(BLESSED_STRIKES_CHOICE_KEY);
  if (getDruidLevel(character) > 0 && !getPrimalOrder(character)) pending.add(PRIMAL_ORDER_CHOICE_KEY);
  if (needsElementalFury(character)) pending.add(ELEMENTAL_FURY_CHOICE_KEY);
  if (needsKnownForms(character, getDruidLevel(character))) pending.add(KNOWN_FORMS_CHOICE_KEY);
  if (needsLand(character)) pending.add(LAND_CHOICE_KEY);
  if (needsSpellbookSpells(character, spellCatalog)) pending.add(SPELLBOOK_CHOICE_KEY);
  if (needsSpellMastery(character)) pending.add(SPELL_MASTERY_CHOICE_KEY);
  if (needsSignatureSpells(character)) pending.add(SIGNATURE_SPELLS_CHOICE_KEY);
  if (needsBonusProficiencies(character)) pending.add(BONUS_PROFICIENCIES_CHOICE_KEY);
  if (needsMagicalDiscoveries(character)) pending.add(MAGICAL_DISCOVERIES_CHOICE_KEY);
  getUnchosenMagicInitiateLists(character).forEach((list) => pending.add(magicInitiateChoiceKey(list)));
  return pending;
};

/**
 * Feature choices that are complete but can still be changed later (light glow):
 * Weapon Mastery can be swapped after a Long Rest; Fighting Style while a change is still allowed.
 */
export const getEditableFeatureChoices = (character) => {
  const editable = new Set(getWeaponMasteryEntitlements(character).map(({ sourceId }) => weaponMasteryChoiceKey(sourceId)));
  fightingStyleClasses.forEach((classId) => {
    if (canChangeClassFightingStyle(character, classId)) editable.add(fightingStyleChoiceKey(classId));
  });
  if (canChangeAdditionalFightingStyle(character)) editable.add(ADDITIONAL_FIGHTING_STYLE_CHOICE_KEY);
  if (getHuntersPrey(character) && canChangeHuntersPrey(character)) editable.add(HUNTERS_PREY_CHOICE_KEY);
  if (getDefensiveTactics(character) && canChangeDefensiveTactics(character)) editable.add(DEFENSIVE_TACTICS_CHOICE_KEY);
  if (getMagicalDiscoveries(character).length && canChangeMagicalDiscoveries(character)) editable.add(MAGICAL_DISCOVERIES_CHOICE_KEY);
  if (getKnownForms(character).length && canSwapKnownForm(character)) editable.add(KNOWN_FORMS_CHOICE_KEY);
  if (getLand(character) && canChangeLand(character)) editable.add(LAND_CHOICE_KEY);
  getMagicInitiateLists(character).forEach((list) => { if (canChangeMagicInitiate(character, list)) editable.add(magicInitiateChoiceKey(list)); });
  return editable;
};

export const hasPendingFeatureChoices = (character) => getPendingFeatureChoices(character).size > 0;
